import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { PageHero } from '../components/layout/PageHero';
import { ProjectCard } from '../components/projects/ProjectCard';
import { ProjectFilters } from '../components/projects/ProjectFilters';
import { Card } from '../components/ui/Card';
import { StatusPill } from '../components/ui/StatusPill';
import { MapPinIcon, BanknoteIcon, CalendarIcon, StarIcon } from 'lucide-react';
import { projects } from '../data/projects';
export function Projects() {
  const [searchQuery, setSearchQuery] = useState('');
  const [sectorFilter, setSectorFilter] = useState('All Sectors');
  const [statusFilter, setStatusFilter] = useState('all');
  const sectors = useMemo(() => Array.from(new Set(projects.map(p => p.sector))).sort(), []);
  const featuredProject = projects.find(p => p.featured);
  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.title.toLowerCase().includes(searchQuery.toLowerCase()) || project.description.toLowerCase().includes(searchQuery.toLowerCase()) || project.countries.some(c => c.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesSector = sectorFilter === 'All Sectors' || project.sector === sectorFilter;
    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
    return matchesSearch && matchesSector && matchesStatus;
  });
  return <div data-id="element-786">
      <PageHero title="Our Projects" subtitle="Transformative initiatives advancing sustainable development across Africa through global partnerships" breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'Projects'
    }]} data-id="element-787" />

      <div className="max-w-7xl mx-auto px-4 py-12" data-id="element-788">
        {featuredProject && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="mb-12" data-id="element-789">
            <div className="flex items-center gap-2 mb-4" data-id="element-790">
              <StarIcon size={20} className="text-au-gold fill-au-gold" data-id="element-791" />
              <h2 className="text-2xl font-serif text-charcoal" data-id="element-792">
                Featured Project
              </h2>
            </div>

            <Card className="border-2 border-au-green overflow-hidden p-0" data-id="element-793">
              <div className="grid md:grid-cols-2" data-id="element-794">
                <div className="bg-au-green p-8 md:p-10 text-white flex flex-col justify-center" data-id="element-795">
                  <span className="inline-block bg-au-gold text-charcoal text-xs font-bold px-3 py-1 rounded-full mb-4 self-start" data-id="element-796">
                    {featuredProject.sector.toUpperCase()}
                  </span>
                  <h3 className="text-3xl md:text-4xl font-serif mb-4 leading-tight" data-id="element-797">
                    {featuredProject.title}
                  </h3>
                  <div className="w-16 h-1 bg-au-gold mb-4" data-id="element-798"></div>
                  <p className="text-au-green-light text-lg leading-relaxed" data-id="element-799">
                    {featuredProject.description}
                  </p>
                </div>

                <div className="p-8 md:p-10 flex flex-col justify-center bg-white" data-id="element-800">
                  <div className="mb-6" data-id="element-801">
                    <StatusPill status={featuredProject.status} data-id="element-802">
                      {featuredProject.status.toUpperCase()}
                    </StatusPill>
                  </div>

                  <div className="space-y-5" data-id="element-803">
                    <div className="flex items-start gap-3" data-id="element-804">
                      <div className="w-10 h-10 rounded-full bg-au-green-light flex items-center justify-center flex-shrink-0" data-id="element-805">
                        <MapPinIcon size={20} className="text-au-green" data-id="element-806" />
                      </div>
                      <div data-id="element-807">
                        <div className="text-sm text-gray-600 mb-1" data-id="element-808">
                          Countries
                        </div>
                        <div className="font-medium text-charcoal" data-id="element-809">
                          {featuredProject.countries.join(', ')}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3" data-id="element-810">
                      <div className="w-10 h-10 rounded-full bg-au-green-light flex items-center justify-center flex-shrink-0" data-id="element-811">
                        <BanknoteIcon size={20} className="text-au-green" data-id="element-812" />
                      </div>
                      <div data-id="element-813">
                        <div className="text-sm text-gray-600 mb-1" data-id="element-814">
                          Total Budget
                        </div>
                        <div className="font-bold text-au-green text-lg" data-id="element-815">
                          {featuredProject.budget}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3" data-id="element-816">
                      <div className="w-10 h-10 rounded-full bg-au-green-light flex items-center justify-center flex-shrink-0" data-id="element-817">
                        <CalendarIcon size={20} className="text-au-green" data-id="element-818" />
                      </div>
                      <div data-id="element-819">
                        <div className="text-sm text-gray-600 mb-1" data-id="element-820">
                          Timeline
                        </div>
                        <div className="font-medium text-charcoal" data-id="element-821">
                          {new Date(featuredProject.startDate).toLocaleDateString('en-GB', {
                        month: 'short',
                        year: 'numeric'
                      })}
                          {featuredProject.endDate && ` – ${new Date(featuredProject.endDate).toLocaleDateString('en-GB', {
                        month: 'short',
                        year: 'numeric'
                      })}`}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>}

        <div className="mb-8" data-id="element-822">
          <h2 className="text-2xl font-serif text-charcoal mb-2" data-id="element-823">
            All Projects
          </h2>
          <div className="w-16 h-1 bg-au-gold" data-id="element-824"></div>
        </div>

        <ProjectFilters searchQuery={searchQuery} setSearchQuery={setSearchQuery} sectorFilter={sectorFilter} setSectorFilter={setSectorFilter} statusFilter={statusFilter} setStatusFilter={setStatusFilter} sectors={sectors} data-id="element-825" />

        <div className="mb-6" data-id="element-826">
          <p className="text-gray-600" data-id="element-827">
            Showing{' '}
            <span className="font-bold text-charcoal" data-id="element-828">
              {filteredProjects.length}
            </span>{' '}
            project{filteredProjects.length !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-829">
          {filteredProjects.map((project, index) => <ProjectCard key={project.id} project={project} index={index} data-id="element-830" />)}
        </div>

        {filteredProjects.length === 0 && <div className="text-center py-16 bg-white rounded-lg border border-gray-border" data-id="element-831">
            <p className="text-gray-600 text-lg mb-2" data-id="element-832">
              No projects match your filters.
            </p>
            <button onClick={() => {
          setSearchQuery('');
          setSectorFilter('All Sectors');
          setStatusFilter('all');
        }} className="text-au-green font-medium hover:underline" data-id="element-833">
              Clear filters
            </button>
          </div>}
      </div>
    </div>;
}